import { useState } from 'react';
import { Button } from 'src/components/Button';
import { Input } from 'src/components/Input';
import { Modal } from 'src/components/Modal';
import { useEnvironment } from 'src/contexts/EnvironmentContext';
import { useNotification } from 'src/contexts/NotificationContext';
import useAuthedRequest from 'src/hooks/useAuthedRequest';
import { Can, Subject } from 'src/hooks/usePermissions';
import { CreateEnvironmentDto } from 'src/interfaces/environment.interface';
import { ApiKeys } from './ApiKeys';

export const EnvironmentsTab = () => {
  const { post: authedPost } = useAuthedRequest();
  const notification = useNotification();
  const { hiddenEnvironmentsIds, fetchEnvironments, environmentList } =
    useEnvironment();

  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [newEnvironment, setNewEnvironment] = useState<CreateEnvironmentDto>({
    name: '',
  });

  const handleCreateEnvironment = () => {
    setModalOpen(true);
  };

  const createEnvironment = async () => {
    setLoading(true);

    try {
      await authedPost(`/environments`, newEnvironment);
      await fetchEnvironments();

      notification.success('Environment created successfully');
      setModalOpen(false);
    } catch {
      notification.error('Failed to create environment');
    } finally {
      setLoading(false);
      setNewEnvironment({ name: '' });
    }
  };

  const handleCloseCreateEnvironmentModal = () => {
    setModalOpen(false);
    setNewEnvironment({ name: '' });
  };

  return (
    <>
      <Can operation="read" subject={Subject.ENVIRONMENTS}>
        <ApiKeys
          hiddenEnvironmentsIds={hiddenEnvironmentsIds}
          fetchEnvironments={fetchEnvironments}
          environmentList={environmentList}
          handleCreateEnvironment={handleCreateEnvironment}
        />
      </Can>

      <Modal
        title="Create Environment"
        isOpen={modalOpen}
        onClose={handleCloseCreateEnvironmentModal}
        actionButton={
          <Button
            onClick={createEnvironment}
            disabled={loading || !newEnvironment || !newEnvironment.name}
          >
            Create Environment
          </Button>
        }
      >
        <div className="flex justify-end italic text-sm">* Required fields</div>

        <div className="grid grid-cols-1 gap-6">
          <div>
            <Input
              label="Name"
              type="text"
              value={newEnvironment.name}
              onChange={(e) =>
                setNewEnvironment({ ...newEnvironment, name: e.target.value })
              }
              required
            />
          </div>
        </div>
      </Modal>
    </>
  );
};
